import PopoverMenuItem from "@entrilia/shared/components/PopoverMenuItem";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Divider, Typography } from "@mui/material";
import type { JSX } from "react";
import { useCallback } from "react";
import type { FieldConfiguration } from "../../../../../../../../../api/types/fieldConfigurationTypes";
import { CalculationType } from "../../../../../../../../../api/types/fieldConfigurationTypes";
import type { CustomColumnField } from "../../../../../../../../../api/types/svTypes";
import CustomLabel from "../../../../viewer/common/fields/CustomLabel";
import FieldOptionDeleteButton from "../../../../viewer/common/fields/FieldOptionDeleteButton";
import CustomId from "../../../api-report/editor/fields/CustomId";
import CustomFieldPredefinedCalculationTypeItem from "./CustomFieldPredefinedCalculationTypeItem";

interface Props {
  field: CustomColumnField;
  otherOptions: JSX.Element;
  isApiReport?: boolean;
  cancel: () => void;
  saveChanges: (changes: Partial<FieldConfiguration>, causeClosing: boolean) => void;
  onRemove: () => void;
  onEditCustomFormulaCalculationField: (fieldGuid: string) => void;
  readonly: boolean;
}
const CustomFieldOptions = ({
  field,
  otherOptions,
  isApiReport = false,
  cancel,
  saveChanges,
  onRemove,
  onEditCustomFormulaCalculationField,
  readonly,
}: Props) => {
  const updateCustomLabel = useCallback(
    (customLabel: string | undefined) => {
      saveChanges({ customLabel }, false);
    },
    [saveChanges]
  );

  const updateCustomId = useCallback(
    (customId: string | undefined) => {
      saveChanges({ customId }, false);
    },
    [saveChanges]
  );

  const handleRemove = useCallback(() => {
    onRemove();
    cancel();
  }, [cancel, onRemove]);

  const handleEditCustomFormulaCalculationField = useCallback(() => {
    onEditCustomFormulaCalculationField(field.config.guid);
  }, [field.config.guid, onEditCustomFormulaCalculationField]);

  return (
    <>
      <CustomLabel
        customFieldValue={field.config.customLabel}
        originalFieldValue={field.meta.caption}
        updateCustomFieldValue={updateCustomLabel}
        readonly={readonly}
      />
      {isApiReport && (
        <CustomId
          customId={field.config.customId}
          defaultId={field.config.guid}
          updateCustomId={updateCustomId}
          readonly={readonly}
        />
      )}
      <OptionDivider />

      {field.config.calculationType && field.config.calculationType !== CalculationType.CustomFormula && (
        <CustomFieldPredefinedCalculationTypeItem field={field} saveChanges={saveChanges} readonly={readonly} />
      )}
      {field.config.calculationType === CalculationType.CustomFormula && !readonly && (
        <PopoverMenuItem sx={{ gap: 1 }} onClick={handleEditCustomFormulaCalculationField}>
          <EditOutlinedIcon color="secondary" />
          <Typography>Edit Calculation</Typography>
        </PopoverMenuItem>
      )}

      <OptionDivider />
      {otherOptions}
      <OptionDivider />
      {!readonly && <FieldOptionDeleteButton onRemove={handleRemove} />}
    </>
  );
};
const OptionDivider = () => <Divider style={{ marginBottom: 0, marginTop: 0 }} />;

export default CustomFieldOptions;
